import { useSupabase } from "../hooks/useSupabase";
import { Post, PostCategory, CreatePostData, UpdatePostData, UpdatePostWithTranslationsData, PostWithCategories, LanguageCode, TranslationInput } from "../types/post";

export const usePostService = () => {
  const supabase = useSupabase();

  // Translation helper functions that use the same supabase instance
  const translationHelpers = {
    async getRecordTranslations(tableName: string, recordId: string) {
      const { data, error } = await supabase
        .from("translations")
        .select("*")
        .eq("table_name", tableName)
        .eq("record_id", recordId);
      
      if (error) throw error;
      return data || [];
    },

    async setRecordTranslations(tableName: string, recordId: string, translations: TranslationInput[]) {
      const promises = translations.map(translation =>
        supabase.rpc('set_translation', {
          p_table_name: tableName,
          p_record_id: recordId,
          p_field_name: translation.field_name,
          p_language_code: translation.language_code,
          p_value: translation.value
        })
      );
      
      await Promise.all(promises);
    },

    formatTranslations(translations: any[]) {
      const result: Record<string, Record<LanguageCode, string>> = {};
      
      translations.forEach(translation => {
        if (!result[translation.field_name]) {
          result[translation.field_name] = {} as Record<LanguageCode, string>;
        }
        (result[translation.field_name] as any)[translation.language_code] = translation.value;
      });
      
      return result;
    },

    getLocalizedText(translations: Record<LanguageCode, string> | undefined, language: LanguageCode = 'pl'): string {
      if (!translations) return '';
      
      // Priority order: requested language, Polish (default), English, Ukrainian, any available
      const availableLanguages: LanguageCode[] = [language, 'pl', 'en', 'uk'];
      
      for (const lang of availableLanguages) {
        if (translations[lang]) {
          return translations[lang];
        }
      }
      
      return '';
    }
  };

  return {
    // Get all posts with their categories
    async getPosts(language: LanguageCode = 'pl'): Promise<PostWithCategories[]> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category(
            category_post(
              id,
              group_key,
              color,
              icon,
              group_category_page_id
            )
          )
        `)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      // Get post IDs for translation lookup
      const postIds = data.map(post => post.id);
      
      // Get category IDs for translation lookup
      const categoryIds = new Set<string>();
      data.forEach((post: any) => {
        post.post_category?.forEach((pc: any) => {
          categoryIds.add(pc.category_post.id);
        });
      });

      // Get translations for all posts and categories
      const postTranslations: Record<string, Record<string, Record<LanguageCode, string>>> = {};
      const categoryTranslations: Record<string, Record<string, Record<LanguageCode, string>>> = {};
      
      if (postIds.length > 0) {
        const { data: postTranslationsData, error: postTranslationsError } = await supabase
          .from("translations")
          .select("*")
          .eq("table_name", "post")
          .in("record_id", postIds);
        
        if (postTranslationsError) throw postTranslationsError;
        
        // Group post translations by post ID
        postTranslationsData?.forEach(translation => {
          if (!postTranslations[translation.record_id]) {
            postTranslations[translation.record_id] = {};
          }
          if (!postTranslations[translation.record_id][translation.field_name]) {
            postTranslations[translation.record_id][translation.field_name] = {} as Record<LanguageCode, string>;
          }
          (postTranslations[translation.record_id][translation.field_name] as any)[translation.language_code] = translation.value;
        });
      }

      if (categoryIds.size > 0) {
        const { data: translations, error: translationsError } = await supabase
          .from("translations")
          .select("*")
          .eq("table_name", "category_post")
          .in("record_id", Array.from(categoryIds));
        
        if (translationsError) throw translationsError;
        
        // Group translations by category ID
        translations?.forEach(translation => {
          if (!categoryTranslations[translation.record_id]) {
            categoryTranslations[translation.record_id] = {};
          }
          if (!categoryTranslations[translation.record_id][translation.field_name]) {
            categoryTranslations[translation.record_id][translation.field_name] = {} as Record<LanguageCode, string>;
          }
          (categoryTranslations[translation.record_id][translation.field_name] as any)[translation.language_code] = translation.value;
        });
      }
      
      // Transform the data to include category names and post subtitles
      return data.map((post: any) => ({
        ...post,
        subtitle: translationHelpers.getLocalizedText(postTranslations[post.id]?.subtitle, language),
        category_names: post.post_category?.map((pc: any) => {
          const categoryId = pc.category_post.id;
          const nameTranslations = categoryTranslations[categoryId]?.name;
          return translationHelpers.getLocalizedText(nameTranslations, language);
        }).filter(Boolean) || []
      }));
    },

    // Get a single post with categories
    async getPost(id: string, language: LanguageCode = 'pl'): Promise<PostWithCategories | null> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category(
            category_post(
              id,
              group_key,
              color,
              icon,
              group_category_page_id
            )
          )
        `)
        .eq("id", id)
        .single();
      
      if (error) {
        if (error.code === "PGRST116") {
          return null; // Not found
        }
        throw error;
      }
      
      // Get translations for the post
      const postTranslations: Record<string, Record<LanguageCode, string>> = {};
      const { data: postTranslationsData, error: postTranslationsError } = await supabase
        .from("translations")
        .select("*")
        .eq("table_name", "post")
        .eq("record_id", id);
      
      if (postTranslationsError) throw postTranslationsError;
      
      // Group post translations by field name
      postTranslationsData?.forEach(translation => {
        if (!postTranslations[translation.field_name]) {
          postTranslations[translation.field_name] = {} as Record<LanguageCode, string>;
        }
        (postTranslations[translation.field_name] as any)[translation.language_code] = translation.value;
      });
      
      // Get category IDs for translation lookup
      const categoryIds = data.post_category?.map((pc: any) => pc.category_post.id) || [];
      
      // Get translations for categories
      const categoryTranslations: Record<string, Record<string, Record<LanguageCode, string>>> = {};
      if (categoryIds.length > 0) {
        const { data: translations, error: translationsError } = await supabase
          .from("translations")
          .select("*")
          .eq("table_name", "category_post")
          .in("record_id", categoryIds);
        
        if (translationsError) throw translationsError;
        
        // Group translations by category ID
        translations?.forEach(translation => {
          if (!categoryTranslations[translation.record_id]) {
            categoryTranslations[translation.record_id] = {};
          }
          if (!categoryTranslations[translation.record_id][translation.field_name]) {
            categoryTranslations[translation.record_id][translation.field_name] = {} as Record<LanguageCode, string>;
          }
          (categoryTranslations[translation.record_id][translation.field_name] as any)[translation.language_code] = translation.value;
        });
      }
      
      return {
        ...data,
        subtitle: translationHelpers.getLocalizedText(postTranslations.subtitle, language),
        category_names: data.post_category?.map((pc: any) => {
          const categoryId = pc.category_post.id;
          const nameTranslations = categoryTranslations[categoryId]?.name;
          return translationHelpers.getLocalizedText(nameTranslations, language);
        }).filter(Boolean) || []
      };
    },

    // Get a single post with all subtitle translations
    async getPostWithTranslations(id: string): Promise<(PostWithCategories & { subtitleTranslations: Record<LanguageCode, string> }) | null> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category(
            category_post(
              id,
              group_key,
              color,
              icon,
              group_category_page_id
            )
          )
        `)
        .eq("id", id)
        .single();
      
      if (error) {
        if (error.code === "PGRST116") {
          return null; // Not found
        }
        throw error;
      }
      
      // Get translations for the post
      const postTranslations: Record<string, Record<LanguageCode, string>> = {};
      const { data: postTranslationsData, error: postTranslationsError } = await supabase
        .from("translations")
        .select("*")
        .eq("table_name", "post")
        .eq("record_id", id);
      
      if (postTranslationsError) throw postTranslationsError;
      
      // Group post translations by field name
      postTranslationsData?.forEach(translation => {
        if (!postTranslations[translation.field_name]) {
          postTranslations[translation.field_name] = {} as Record<LanguageCode, string>;
        }
        (postTranslations[translation.field_name] as any)[translation.language_code] = translation.value;
      });
      
      // Get category IDs for translation lookup
      const categoryIds = data.post_category?.map((pc: any) => pc.category_post.id) || [];
      
      // Get translations for categories
      const categoryTranslations: Record<string, Record<string, Record<LanguageCode, string>>> = {};
      if (categoryIds.length > 0) {
        const { data: translations, error: translationsError } = await supabase
          .from("translations")
          .select("*")
          .eq("table_name", "category_post")
          .in("record_id", categoryIds);
        
        if (translationsError) throw translationsError;
        
        // Group translations by category ID
        translations?.forEach(translation => {
          if (!categoryTranslations[translation.record_id]) {
            categoryTranslations[translation.record_id] = {};
          }
          if (!categoryTranslations[translation.record_id][translation.field_name]) {
            categoryTranslations[translation.record_id][translation.field_name] = {} as Record<LanguageCode, string>;
          }
          (categoryTranslations[translation.record_id][translation.field_name] as any)[translation.language_code] = translation.value;
        });
      }
      
      return {
        ...data,
        subtitle: translationHelpers.getLocalizedText(postTranslations.subtitle, 'pl'),
        subtitleTranslations: postTranslations.subtitle || { en: '', pl: '', uk: '' },
        category_names: data.post_category?.map((pc: any) => {
          const categoryId = pc.category_post.id;
          const nameTranslations = categoryTranslations[categoryId]?.name;
          return translationHelpers.getLocalizedText(nameTranslations, 'pl');
        }).filter(Boolean) || []
      };
    },

    // Get all post categories with translations
    async getPostCategories(language: LanguageCode = 'pl'): Promise<PostCategory[]> {
      const { data, error } = await supabase
        .from("category_post")
        .select("*")
        .order("group_key");
      
      if (error) throw error;
      
      // Get translations for all categories
      const categoryIds = data.map(category => category.id);
      const categoryTranslations: Record<string, Record<string, Record<LanguageCode, string>>> = {};
      
      if (categoryIds.length > 0) {
        const { data: translations, error: translationsError } = await supabase
          .from("translations")
          .select("*")
          .eq("table_name", "category_post")
          .in("record_id", categoryIds);
        
        if (translationsError) throw translationsError;
        
        // Group translations by category ID
        translations?.forEach(translation => {
          if (!categoryTranslations[translation.record_id]) {
            categoryTranslations[translation.record_id] = {};
          }
          if (!categoryTranslations[translation.record_id][translation.field_name]) {
            categoryTranslations[translation.record_id][translation.field_name] = {} as Record<LanguageCode, string>;
          }
          (categoryTranslations[translation.record_id][translation.field_name] as any)[translation.language_code] = translation.value;
        });
      }
      
      // Add localized names to categories
      return data.map(category => ({
        ...category,
        name: translationHelpers.getLocalizedText(categoryTranslations[category.id]?.name, language),
        description: translationHelpers.getLocalizedText(categoryTranslations[category.id]?.description, language)
      }));
    },

    // Create a new post category with translations
    async createPostCategory(
      name: string, 
      description: string = '',
      groupCategoryPageId?: string,
      language: LanguageCode = 'pl'
    ): Promise<PostCategory> {
      const { data, error } = await supabase
        .from("category_post")
        .insert({ group_category_page_id: groupCategoryPageId })
        .select()
        .single();
      
      if (error) throw error;
      
      // Set translations for name and description
      const translations: TranslationInput[] = [
        {
          field_name: 'name',
          language_code: language,
          value: name
        }
      ];
      
      if (description) {
        translations.push({
          field_name: 'description',
          language_code: language,
          value: description
        });
      }
      
      await translationHelpers.setRecordTranslations('category_post', data.id, translations);
      
      return {
        ...data,
        name,
        description
      };
    },    // Create a new post with categories and translations
    async createPost(postData: CreatePostData, language: LanguageCode = 'pl'): Promise<Post> {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Create the post (without subtitle in database)
      const { data: post, error: postError } = await supabase
        .from("post")
        .insert({
          title: postData.title,
          content: postData.content,
          is_public: postData.is_public,
          user_id: user.id
        })
        .select()
        .single();

      if (postError) throw postError;

      // Set subtitle translation if provided
      if (postData.subtitle) {
        const subtitleTranslation: TranslationInput = {
          field_name: 'subtitle',
          language_code: language,
          value: postData.subtitle
        };
        
        await translationHelpers.setRecordTranslations('post', post.id, [subtitleTranslation]);
      }

      // Add category relationships if provided
      if (postData.category_ids && postData.category_ids.length > 0) {
        const categoryRelations = postData.category_ids.map(categoryId => ({
          post_id: post.id,
          category_id: categoryId
        }));

        const { error: relationError } = await supabase
          .from("post_category")
          .insert(categoryRelations);
        
        if (relationError) throw relationError;
      }

      return {
        ...post,
        subtitle: postData.subtitle
      };
    },

    // Update a post and its categories and translations
    async updatePost(id: string, postData: UpdatePostData, language: LanguageCode = 'pl'): Promise<Post> {
      // Update the post (without subtitle in database)
      const { data: post, error: postError } = await supabase
        .from("post")
        .update({
          title: postData.title,
          content: postData.content,
          is_public: postData.is_public
        })
        .eq("id", id)
        .select()
        .single();
      
      if (postError) throw postError;

      // Update subtitle translation if provided
      if (postData.subtitle !== undefined) {
        if (postData.subtitle) {
          // Set or update subtitle translation
          const subtitleTranslation: TranslationInput = {
            field_name: 'subtitle',
            language_code: language,
            value: postData.subtitle
          };
          
          await translationHelpers.setRecordTranslations('post', id, [subtitleTranslation]);
        } else {
          // Remove subtitle translation if empty
          const { error: deleteError } = await supabase
            .from("translations")
            .delete()
            .eq("table_name", "post")
            .eq("record_id", id)
            .eq("field_name", "subtitle")
            .eq("language_code", language);
          
          if (deleteError) throw deleteError;
        }
      }

      // Update category relationships if provided
      if (postData.category_ids !== undefined) {
        // Remove existing category relationships
        const { error: deleteError } = await supabase
          .from("post_category")
          .delete()
          .eq("post_id", id);
        
        if (deleteError) throw deleteError;

        // Add new category relationships
        if (postData.category_ids.length > 0) {
          const categoryRelations = postData.category_ids.map(categoryId => ({
            post_id: id,
            category_id: categoryId
          }));

          const { error: relationError } = await supabase
            .from("post_category")
            .insert(categoryRelations);
          
          if (relationError) throw relationError;
        }
      }

      return {
        ...post,
        subtitle: postData.subtitle
      };
    },

    // Update a post with multi-language subtitle translations
    async updatePostWithTranslations(id: string, postData: UpdatePostWithTranslationsData): Promise<Post> {
      // Update the post (without subtitle in database)
      const { data: post, error: postError } = await supabase
        .from("post")
        .update({
          title: postData.title,
          content: postData.content,
          is_public: postData.is_public
        })
        .eq("id", id)
        .select()
        .single();
      
      if (postError) throw postError;

      // Update subtitle translations if provided
      if (postData.subtitleTranslations !== undefined) {
        const translations: TranslationInput[] = [];
        
        // Collect all translations for subtitle
        Object.entries(postData.subtitleTranslations).forEach(([langCode, value]) => {
          if (typeof value === 'string' && value.trim()) {
            translations.push({
              field_name: 'subtitle',
              language_code: langCode as LanguageCode,
              value: value.trim()
            });
          }
        });

        // First, remove all existing subtitle translations for this post
        const { error: deleteError } = await supabase
          .from("translations")
          .delete()
          .eq("table_name", "post")
          .eq("record_id", id)
          .eq("field_name", "subtitle");
        
        if (deleteError) throw deleteError;

        // Then add the new translations if any
        if (translations.length > 0) {
          await translationHelpers.setRecordTranslations('post', id, translations);
        }
      }

      // Update category relationships if provided
      if (postData.category_ids !== undefined) {
        // Remove existing category relationships
        const { error: deleteError } = await supabase
          .from("post_category")
          .delete()
          .eq("post_id", id);
        
        if (deleteError) throw deleteError;

        // Add new category relationships
        if (postData.category_ids.length > 0) {
          const categoryRelations = postData.category_ids.map((categoryId: string) => ({
            post_id: id,
            category_id: categoryId
          }));

          const { error: relationError } = await supabase
            .from("post_category")
            .insert(categoryRelations);
          
          if (relationError) throw relationError;
        }
      }

      return post;
    },

    // Create a new post with multi-language subtitle translations
    async createPostWithTranslations(postData: { title: string; subtitleTranslations: Record<LanguageCode, string>; content?: string; is_public: boolean; category_ids?: string[] }): Promise<Post> {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Create the post (without subtitle in database)
      const { data: post, error: postError } = await supabase
        .from("post")
        .insert({
          title: postData.title,
          content: postData.content,
          is_public: postData.is_public,
          user_id: user.id
        })
        .select()
        .single();

      if (postError) throw postError;

      // Set subtitle translations if provided
      if (postData.subtitleTranslations) {
        const translations: TranslationInput[] = [];
        
        // Collect all translations for subtitle
        Object.entries(postData.subtitleTranslations).forEach(([langCode, value]) => {
          if (typeof value === 'string' && value.trim()) {
            translations.push({
              field_name: 'subtitle',
              language_code: langCode as LanguageCode,
              value: value.trim()
            });
          }
        });

        // Add the translations if any
        if (translations.length > 0) {
          await translationHelpers.setRecordTranslations('post', post.id, translations);
        }
      }

      // Add category relationships if provided
      if (postData.category_ids && postData.category_ids.length > 0) {
        const categoryRelations = postData.category_ids.map((categoryId: string) => ({
          post_id: post.id,
          category_id: categoryId
        }));

        const { error: relationError } = await supabase
          .from("post_category")
          .insert(categoryRelations);
        
        if (relationError) throw relationError;
      }

      return post;
    },

    // Delete a post (categories will be deleted automatically due to CASCADE)
    async deletePost(id: string): Promise<void> {
      const { error } = await supabase
        .from("post")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },

    // Delete a post category
    async deletePostCategory(id: string): Promise<void> {
      const { error } = await supabase
        .from("category_post")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },

    // Get posts by category
    async getPostsByCategory(categoryId: string, language: LanguageCode = 'pl'): Promise<PostWithCategories[]> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category!inner(
            category_post!inner(
              id,
              group_key,
              color,
              icon,
              group_category_page_id
            )
          )
        `)
        .eq("post_category.category_id", categoryId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      // Get post IDs for subtitle translation lookup
      const postIds = data.map((post: any) => post.id);
      
      // Get subtitle translations for all posts
      const postTranslations: Record<string, Record<string, Record<LanguageCode, string>>> = {};
      if (postIds.length > 0) {
        const { data: postTranslationsData, error: postTranslationsError } = await supabase
          .from("translations")
          .select("*")
          .eq("table_name", "post")
          .in("record_id", postIds);
        
        if (postTranslationsError) throw postTranslationsError;
        
        // Group post translations by post ID and field name
        postTranslationsData?.forEach(translation => {
          if (!postTranslations[translation.record_id]) {
            postTranslations[translation.record_id] = {};
          }
          if (!postTranslations[translation.record_id][translation.field_name]) {
            postTranslations[translation.record_id][translation.field_name] = {} as Record<LanguageCode, string>;
          }
          (postTranslations[translation.record_id][translation.field_name] as any)[translation.language_code] = translation.value;
        });
      }
      
      // Get category IDs for translation lookup
      const categoryIds = new Set<string>();
      data.forEach((post: any) => {
        post.post_category?.forEach((pc: any) => {
          categoryIds.add(pc.category_post.id);
        });
      });

      // Get translations for all categories
      const categoryTranslations: Record<string, Record<string, Record<LanguageCode, string>>> = {};
      if (categoryIds.size > 0) {
        const { data: translations, error: translationsError } = await supabase
          .from("translations")
          .select("*")
          .eq("table_name", "category_post")
          .in("record_id", Array.from(categoryIds));
        
        if (translationsError) throw translationsError;
        
        // Group translations by category ID
        translations?.forEach(translation => {
          if (!categoryTranslations[translation.record_id]) {
            categoryTranslations[translation.record_id] = {};
          }
          if (!categoryTranslations[translation.record_id][translation.field_name]) {
            categoryTranslations[translation.record_id][translation.field_name] = {} as Record<LanguageCode, string>;
          }
          (categoryTranslations[translation.record_id][translation.field_name] as any)[translation.language_code] = translation.value;
        });
      }
      
      return data.map((post: any) => ({
        ...post,
        subtitle: translationHelpers.getLocalizedText(postTranslations[post.id]?.subtitle, language),
        category_names: post.post_category?.map((pc: any) => {
          const categoryId = pc.category_post.id;
          const nameTranslations = categoryTranslations[categoryId]?.name;
          return translationHelpers.getLocalizedText(nameTranslations, language);
        }).filter(Boolean) || []
      }));
    }
  };
};